import {
  Box,
  Tab as ChakraTab,
  Table,
  TableCaption,
  TableContainer,
  TabPanel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { calcVarianceDecimal } from '@lib/calculations/variance-decimal';
import is from '@sindresorhus/is';
import type {
  ActualCostOverviewSummary,
  EstimatedCostOverviewSummary,
} from '@snap-types/actions/show-cost-overview';
import { Decimal } from '@snap-types/types/decimal';
import { Alert } from '@ui/components/Alert';
import Loading from '@ui/components/Loading';
import { styles } from '@ui/components/TanstackTable';
import { useQuoteLineContext } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineProvider';
import { useShowCostOverviewQuery } from '@ui/features/quote-builder/state/endpoints/show-cost-overview';
import { actions } from '@ui/features/quote-builder/state/reducer';
import { selectQuoteLineWithLeadTimeIssues } from '@ui/features/quote-builder/state/selectors/quote-line';
import { QuoteInfoTab } from '@ui/features/quote-builder/types/state/quote-line';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { isApiResponseError } from '@ui/util/api-response-errors';
import { currency } from '@ui/util/currency';
import { monthDayYear } from '@ui/util/dates';

import { TabComponentType } from './types';

const VARIANCE_TOLERANCE = 15;
const LOADING_TEXT = 'Loading cost overview';
const EMPTY_MESSAGE = 'No recent cost data available';
const DEFAULT_ERROR_MESSAGE = 'Failed to fetch cost overview data';
const ERROR_ALERT_TITLE = 'Sorry, something went wrong';

const costs = [
  { property: 'grossUnitCost', name: 'Gross Unit Cost' },
  { property: 'contributionUnitCost', name: 'Contribution Unit Cost' },
  { property: 'materialUnitCost', name: 'Material Unit Cost' },
  { property: 'laborUnitCost', name: 'Labor Unit Cost' },
  { property: 'serviceUnitCost', name: 'Service Unit Cost' },
  { property: 'burdenUnitCost', name: 'Burden Unit Cost' },
] as { property: keyof ActualCostOverviewSummary; name: string }[];

const tab = QuoteInfoTab.CostOverview;

function Tab() {
  const dispatch = useDispatch();
  const { quoteLineUuid } = useQuoteLineContext();

  function handleTabChange() {
    dispatch(actions.quoteLineInfoTabChanged({ quoteLineUuid, tab }));
  }

  return <ChakraTab onClick={handleTabChange}>Cost Overview</ChakraTab>;
}

function CostOverviewPanel() {
  const currentSite = useCurrentSite();
  const { quoteLineUuid } = useQuoteLineContext();
  const { partId } = useSelector((state) =>
    selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid),
  );

  const { data, error, isLoading, isSuccess, isFetching, isError } =
    useShowCostOverviewQuery({
      siteCode: currentSite.code,
      partId,
    });

  if (isLoading) {
    return (
      <Box p={4}>
        <Loading loadingText={LOADING_TEXT} />
      </Box>
    );
  }

  if (isSuccess) {
    const { estimated, actual } = data;

    return (
      <CostOverviewTable
        estimated={estimated}
        actual={actual}
        isFetching={isFetching}
      />
    );
  }

  if (isError) {
    let message = DEFAULT_ERROR_MESSAGE;

    if (isApiResponseError(error)) {
      message = error.data.message;

      /**
       * A 422 response means there's likely no Part Engineering data with which
       * to generate the cost comparison.
       */
      if (error.status === 422) {
        return <Text>{EMPTY_MESSAGE}</Text>;
      }
    }

    return (
      <Alert status="error" title={ERROR_ALERT_TITLE} description={message} />
    );
  }

  return (
    <Alert
      status="error"
      title={ERROR_ALERT_TITLE}
      description={DEFAULT_ERROR_MESSAGE}
    />
  );
}

function Panel() {
  return (
    <TabPanel>
      <CostOverviewPanel />
    </TabPanel>
  );
}

type CostOverviewTableProps = {
  actual: ActualCostOverviewSummary;
  estimated: EstimatedCostOverviewSummary;
  isFetching?: boolean;
};

/**
 * @TODO(shawk): Map data into rows and use <TanstackTable />?
 *
 * This presently uses the older table components, but could be updated to use
 * the newer <TanstackTable /> component, if it makes sense.
 */
function CostOverviewTable({
  estimated,
  actual,
  isFetching = false,
}: CostOverviewTableProps) {
  return (
    <>
      <TableContainer
        width="100%"
        transition="opacity 100ms linear"
        opacity={isFetching ? 0.5 : 1}
        mb={4}
      >
        <Table width="100%" sx={styles} size="sm">
          <TableCaption fontSize="md" color="black">
            Based on most recent costs for work order {estimated.baseId} created
            on {monthDayYear(estimated.createdAt.toISOString(), 'MM/dd/yyyy')}.
          </TableCaption>

          <Thead>
            <Tr>
              <Th width="25%"></Th>
              <Th>Estimated</Th>
              <Th>Actual</Th>
              <Th>Variance (%)</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Quantity</Td>
              <Td>{estimated.quantity.toNumber()}</Td>
              <Td>{actual.quantity.toNumber()}</Td>
              <Td>
                <Variance
                  estimated={estimated.quantity}
                  actual={actual.quantity}
                />
              </Td>
            </Tr>

            {costs.map(({ property, name }) => (
              <Tr key={property}>
                <Td>{name}</Td>
                <Td>{currency(estimated[property])}</Td>
                <Td>{currency(actual[property])}</Td>
                <Td>
                  <Variance
                    estimated={estimated[property]}
                    actual={actual[property]}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
}

type VarianceProps = {
  estimated?: Decimal;
  actual: Decimal;
};

function Variance({ estimated, actual }: VarianceProps) {
  if (estimated === undefined) {
    return <>-</>;
  }

  const variance = calcVarianceDecimal(estimated, actual);
  let color = undefined;

  if (variance.abs().greaterThanOrEqualTo(new Decimal(VARIANCE_TOLERANCE))) {
    color = '#ff0000';
  }

  return (
    <Text color={color}>
      {is.infinite(variance) ? '~' : variance.toNumber() || 0}%
    </Text>
  );
}

export const CostOverview = {
  tab,
  Tab,
  Panel,
} satisfies TabComponentType;
